import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useUser } from "../../contexts/UserContext";
import { Lock, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authService } from "../../services/authService";

interface LoginFormProps {
  onLogin?: () => void;
}

export function LoginForm({ onLogin }: LoginFormProps) {
  const [rememberMe, setRememberMe] = useState(() => {
    const stored = localStorage.getItem('rememberMe');
    return stored === null ? false : stored === 'true';
  });
  const [username, setUsername] = useState(() => {
    const storedRemember = localStorage.getItem('rememberMe');
    const storedUsername = localStorage.getItem('rememberedUsername');
    return storedRemember === 'true' && storedUsername ? storedUsername : '';
  });
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const { setCurrentUser } = useUser();
  const navigate = useNavigate();
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);

    // Simple validation
    if (!username || !password) {
      setLoginError("Please enter both username and password");
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.login({ username, password });
      
      // Role check
      const allowedRoles = ["distributor_admin", "corporate_admin"];
      if (!allowedRoles.includes(response.user.role)) {
        setLoginError("You dont have access to login");
        toast.error("You dont have access to login");
        setIsLoading(false);
        return;
      }
      
      // Clear any existing tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      // Store token based on rememberMe
      if (rememberMe) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('rememberedUsername', username);
      } else {
        sessionStorage.setItem('token', response.token);
        localStorage.removeItem('rememberedUsername');
      }
      // Persist rememberMe state
      localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');

      // Set user in context
      setCurrentUser(response.user);
      
      if (onLogin) {
        onLogin();
      }

      toast.success("Login successful!");
      navigate('/dashboard');
    } catch (error: any) {
      setLoginError(error.message || "Login failed. Please try again.");
      toast.error("Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  // When rememberMe changes, persist it and handle username/password storage
  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');
    if (!rememberMe) {
      localStorage.removeItem('rememberedUsername');
    } else {
      if (username) localStorage.setItem('rememberedUsername', username);
    }
  }, [rememberMe]);

  // When username changes and rememberMe is checked, update localStorage
  useEffect(() => {
    if (rememberMe && username) {
      localStorage.setItem('rememberedUsername', username);
    }
  }, [username, rememberMe]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center items-center p-6">
            <img 
              src="/logo/login_header.jpg" 
              alt="EPaathshaala Logo" 
              className="h-24 w-auto object-contain hover:scale-105 transition-transform duration-200"
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                  autoComplete="off"
                  ref={usernameRef}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(!!checked)}
                disabled={isLoading}
              />
              <Label htmlFor="remember" className="text-sm">
                Remember me
              </Label>
            </div>

            {loginError && (
              <div className="text-sm text-red-600 text-center">
                {loginError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 